import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { FaMagnifyingGlass } from 'react-icons/fa6';
import { GrRevert } from 'react-icons/gr';
import { FaPlus } from 'react-icons/fa';
import { MdModeEdit } from 'react-icons/md';

import { useAppContext } from '../contextApi/context';
import { getIssueMovements, getIssueMovementsTotalValue } from '../services/StockMovementService';
import { exportIssues } from '../services/ExportService';
import EditMovementReturnModal from '../components/EditMovementReturnModal';
import MovementReturnModal from '../components/MovementReturnModal';
import StockMovementDetailsModal from '../components/shared/StockMovementDetailsModal';
import TableContainer from '../components/table/TableContainer';
import { TableActionButton, AddButton } from '../styles/StyledComponents';
import { formatDateAndTime } from '../utils/dates';
import { formatCurrency } from '../utils/numbers';
import { checkPermission } from '../utils/permissions';

const Issues = () => {
    // ----- Context/Hooks
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { loggedUser } = useAppContext();

    // ----- State
    let [movementId, setMovementId] = useState(null);
    let [showDetailsModal, setShowDetailsModal] = useState(false);
    let [showReturnModal, setShowReturnModal] = useState(false);
    let [showEditReturnModal, setShowEditReturnModal] = useState(false);
    let [showMovementCreated, setShowMovementCreated] = useState(false);
    let [showMovementEdited, setShowMovementEdited] = useState(false);
    const [updateTable, setUpdateTable] = useState(false);
    let [tableTotalValue, setTableTotalValue] = useState(null);

    // ----- API Fetch Actions
    const fetchTotalValueData = useCallback(
        async (filters = []) => {
            return await getIssueMovementsTotalValue(loggedUser.token, filters);
        },
        [loggedUser.token]
    );

    // ----- Hooks
    useEffect(() => {
        fetchTotalValueData().then((data) => {
            setTableTotalValue(data);
        });

        if (searchParams.get('created')) {
            setShowMovementCreated(true);
        }
    }, [searchParams, fetchTotalValueData]);

    // ----- Actions
    const handleShowDetails = (id) => {
        setMovementId(id);
        setShowDetailsModal(true);
    };

    const handleCloseDetailsModal = () => {
        setMovementId(null);
        setShowDetailsModal(false);
    };

    const handleShowReturnModal = (id) => {
        setMovementId(id);
        setShowReturnModal(true);
    };

    const handleShowEditReturnModal = (id) => {
        setMovementId(id);
        setShowEditReturnModal(true);
    };

    const handleCloseReturnModal = () => {
        setMovementId(null);
        setShowReturnModal(false);
    };

    const handleCloseEditReturnModal = () => {
        setMovementId(null);
        setShowEditReturnModal(false);
    };

    const handleReturn = () => {
        setShowMovementCreated(false);
        setShowReturnModal(false);
        setUpdateTable(!updateTable);
    };

    const handleEditReturn = () => {
        setShowMovementEdited(true);
        setShowEditReturnModal(false);
        setUpdateTable(!updateTable);
    };

    const handleDataAfterFiltering = (filters) => {
        fetchTotalValueData(filters).then((data) => {
            setTableTotalValue(data);
        });
    };

    // ----- Constants
    const Actions = ({ row }) => (
        <div className='d-flex'>
            <TableActionButton className='btn btn-primary' onClick={() => handleShowDetails(row.id)}>
                <FaMagnifyingGlass />
                Detalles
            </TableActionButton>

            <OverlayTrigger
                delay={{ hide: 200, show: 300 }}
                overlay={(props) => <Tooltip {...props}>{row.returnMovement === null ? 'Hacer una devolución' : 'Editar la devolución'}</Tooltip>}
                placement='top'
            >
                <TableActionButton
                    className='btn btn-primary'
                    onClick={row.returnMovement === null ? () => handleShowReturnModal(row.id) : () => handleShowEditReturnModal(row.returnMovement)}
                >
                    {row.returnMovement === null ? <GrRevert /> : <MdModeEdit />}
                    Editar
                </TableActionButton>
            </OverlayTrigger>
        </div>
    );

    const columns = [
        {
            name: 'Fecha de despacho',
            selector: (row) => (row.eventDate ? formatDateAndTime(row.eventDate) : '--'),
            sortable: true,
            sortField: 'eventDate',
            width: '185px',
        },
        {
            name: 'Despachador',
            selector: (row) => row.user,
            sortable: true,
            sortField: 'user',
        },
        {
            name: 'Solicitante',
            selector: (row) => row.receivingUser,
            sortable: true,
            sortField: 'receivingUser',
        },
        {
            name: 'Placa',
            selector: (row) => row.plate,
            sortable: true,
            sortField: 'plate',
            width: '150px',
        },
        {
            name: 'Valor total',
            selector: (row) => `₡${formatCurrency(row.totalValueWithReturn)}`,
            sortable: true,
            sortField: 'totalValueWithReturn',
            width: '170px',
        },
        {
            name: 'Acciones',
            width: '200px',
            cell: (row) => <Actions row={row} />,
        },
    ];

    const filters = [
        {
            column: 'issueType',
            input: 'text',
            label: 'Tipo de despacho',
            operation: 'like',
            length: 50,
        },
        {
            column: 'returnMovement',
            input: 'select',
            label: 'Estado',
            options: [
                { value: 'Con devolución', text: 'Con devolución', operation: 'notnull' },
                { value: 'Sin devolución', text: 'Sin devolución', operation: 'isnull' },
            ],
            operation: 'equal',
        },
        {
            column: 'article',
            input: 'text',
            label: 'Artículo',
            operation: 'like',
            length: 50,
        },
        {
            column: 'user',
            input: 'text',
            label: 'Despachador',
            operation: 'like',
            length: 50,
        },
        {
            column: 'receivingUser',
            input: 'text',
            label: 'Solicitante',
            operation: 'like',
            length: 50,
        },
        {
            column: 'vehicleType',
            input: 'select',
            label: 'Tipo de vehículo',
            options: [
                { value: 'Alquilado', text: 'Alquilado' },
                { value: 'Amarillo', text: 'Amarillo' },
                { value: 'Cabezal', text: 'Cabezal' },
                { value: 'Liviano', text: 'Liviano' },
                { value: 'Remolque', text: 'Remolque' },
            ],
            operation: 'equal',
        },
        {
            column: 'plate',
            input: 'text',
            label: 'Placa',
            operation: 'like',
            length: 20,
        },
        {
            column: 'totalValueWithReturn',
            input: 'range',
            label: 'Valor total',
            operation: 'between',
            length: 15,
        },
        {
            column: 'eventDate',
            input: 'dates',
            label: 'Fecha de despacho',
            operation: 'between',
        },
    ];

    const exportOptions = checkPermission(loggedUser.permissions, 'export_stock_issue')
        ? {
              handler: exportIssues,
          }
        : null;

    // ----- Render
    return (
        <div className='container mt-5'>
            <h1 className=''>Despacho</h1>

            {showMovementCreated && (
                <div className='alert alert-success text-center mt-3' role='alert'>
                    Despacho creado satisfactoriamente.
                </div>
            )}

            {showMovementEdited && (
                <div className='alert alert-success text-center mt-3' role='alert'>
                    Despacho editado satisfactoriamente.
                </div>
            )}

            {checkPermission(loggedUser.permissions, 'manage_stock_issue') && (
                <div className='d-flex my-3 justify-content-end'>
                    <AddButton className='btn btn-primary' onClick={() => navigate('/crear-despacho')}>
                        <FaPlus /> Nuevo despacho
                    </AddButton>
                </div>
            )}

            <TableContainer
                afterFilteringHandler={handleDataAfterFiltering}
                columns={columns}
                fetchDataHandler={getIssueMovements}
                updateTable={updateTable}
                filters={filters}
                exportOptions={exportOptions}
                totalValue={tableTotalValue}
            />

            {showDetailsModal && <StockMovementDetailsModal movementId={movementId} closeHandler={handleCloseDetailsModal} show={showDetailsModal} />}

            {showReturnModal && (
                <MovementReturnModal
                    movementId={movementId}
                    closeHandler={handleCloseReturnModal}
                    actionHandler={handleReturn}
                    show={showReturnModal}
                />
            )}

            {showEditReturnModal && (
                <EditMovementReturnModal
                    returnId={movementId}
                    closeHandler={handleCloseEditReturnModal}
                    actionHandler={handleEditReturn}
                    show={showEditReturnModal}
                />
            )}
        </div>
    );
};

export default Issues;
